import { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';
import { ServiceType } from '../services/api';

const coreService = apiService.getService(ServiceType.CORE);

interface UseBalanceInfoOptions {
    selectedPortfolioId: string | null;
    onPurchasingPowerFetched?: (purchasingPower: number) => void;
}

export const useBalanceInfo = ({ selectedPortfolioId, onPurchasingPowerFetched }: UseBalanceInfoOptions) => {
    const [purchasingPower, setPurchasingPower] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const callbackRef = useRef(onPurchasingPowerFetched);

    // Update callback ref when it changes
    useEffect(() => {
        callbackRef.current = onPurchasingPowerFetched;
    }, [onPurchasingPowerFetched]);

    useEffect(() => {
        const fetchPurchasingPower = async () => {
            if (!selectedPortfolioId) {
                setPurchasingPower(0);
                if (callbackRef.current) {
                    callbackRef.current(0);
                }
                return;
            }

            try {
                setLoading(true);
                const token = localStorage.getItem('access_token');
                if (!token) {
                    return;
                }

                const brokerCode = 'SSI';
                const response = await coreService.get('/balance', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    },
                    params: {
                        brokerCode,
                        portfolioId: selectedPortfolioId
                    }
                });

                const { purchasingpower } = response.data || {};
                const power = purchasingpower ?? 0;
                setPurchasingPower(power);
                
                // Call the callback if provided
                if (callbackRef.current) {
                    callbackRef.current(power);
                }
            } catch (error) {
                console.error('Error fetching purchasing power:', error);
                setPurchasingPower(0);
                if (callbackRef.current) {
                    callbackRef.current(0);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPurchasingPower();
    }, [selectedPortfolioId]);

    return {
        purchasingPower,
        loading
    };
};

